import { createSlice } from '@reduxjs/toolkit';

import ASYNC_STATE from '../../constants/AsyncState';
import { byId, byKey } from '../../helper/byKey';
import mergeObjects from '../../helper/mergeObjects';

import { allChannelsFetched } from '../channels/channelsThunks/allChannelsFetched';
import { createMemberEmail } from './membersThunks/createMemberEmail';
import { fetchMemberEmail } from './membersThunks/fetchMemberEmail';

const asyncState = {
  memberEmailFetch: {
    status: ASYNC_STATE.IDLE,
    error: null,
  },
  memberEmailCreate: {
    status: ASYNC_STATE.IDLE,
    error: null,
  },
};

const initialState = {
  ...asyncState,
  members: {},
  memberEmails: {},
  channelMembers: {},
};

const persistWhitelist = ['members', 'memberEmails', 'channelMembers'];

const membersSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    memberUpdated: (state, action) => {
      const {
        member,
        member: { id },
      } = action.payload;
      state.members[id] = mergeObjects(state.members[id], member);
    },
    channelMemberRemoved: (state, action) => {
      const {
        channelId,
        member: { id: memberId },
      } = action.payload;
      const channelMembers = state.channelMembers[channelId];

      if (channelMembers) {
        state.channelMembers[channelId] = channelMembers.filter((id) => id !== memberId);
      }
    },
    channelMemberFetched: (state, action) => {
      const {
        channelId,
        member,
        member: { id: memberId },
      } = action.payload;
      state.members[memberId] = member;
      const channelMembers = state.channelMembers[channelId];
      if (!channelMembers) {
        state.channelMembers[channelId] = [memberId];
      } else {
        state.channelMembers[channelId] = [...new Set([...state.channelMembers[channelId], memberId])];
      }
    },
    resetMembersTyping: (state) => {
      for (const id in state.members) {
        state.members[id].isTyping = false;
      }
    },
  },
  extraReducers: {
    [fetchMemberEmail.pending]: (state) => {
      state.memberEmailFetch.status = ASYNC_STATE.LOADING;
    },
    [fetchMemberEmail.fulfilled]: (state, action) => {
      const { memberId, email } = action.payload;
      state.memberEmailFetch.status = ASYNC_STATE.SUCCEEDED;
      if (email) {
        state.memberEmails[memberId] = email;
      }
    },
    [fetchMemberEmail.rejected]: (state, action) => {
      state.memberEmailFetch.status = ASYNC_STATE.FAILED;
      state.error = action.error;
    },
    [createMemberEmail.pending]: (state) => {
      state.memberEmailCreate.status = ASYNC_STATE.LOADING;
    },
    [createMemberEmail.fulfilled]: (state, action) => {
      const { memberId, email } = action.payload;
      state.memberEmailCreate.status = ASYNC_STATE.SUCCEEDED;
      if (email) {
        state.memberEmails[memberId] = email;
      }
    },
    [createMemberEmail.rejected]: (state, action) => {
      state.memberEmailCreate.status = ASYNC_STATE.FAILED;
      state.error = action.error;
    },
    [allChannelsFetched.fulfilled]: (state, action) => {
      const members = action.payload?.members ?? [];

      state.members = mergeObjects(state.members, byId(members));
      const membersByChannelId = byKey('channelId', members, true);
      for (const [channelId, membersForChannel] of Object.entries(membersByChannelId)) {
        state.channelMembers[channelId] = membersForChannel.map((member) => member.id);
      }
    },
  },
});

export { persistWhitelist as membersPersistWhitelist };
export const { memberUpdated, channelMemberRemoved, channelMemberFetched, resetMembersTyping } = membersSlice.actions;
export default membersSlice.reducer;
